import React, { createContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, formatApiErrorDetail } from "./api";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const logoutRef = useRef(null);
  const interceptorRegistered = useRef(false);

  useEffect(() => {
    if (interceptorRegistered.current) return;
    interceptorRegistered.current = true;
    axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err.response?.status;
        const url = err.config?.url || "";
        // Auto-logout on expired/invalid token, but do NOT trigger on the login/register endpoints themselves.
        if (
          status === 401 &&
          !url.includes("/auth/login") &&
          !url.includes("/auth/register") &&
          logoutRef.current
        ) {
          logoutRef.current();
        }
        return Promise.reject(err);
      }
    );
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const newToken = response.data.access_token;
      localStorage.setItem("token", newToken);
      setToken(newToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      return { success: true, error: null };
    } catch (error) {
      console.error("Login failed:", error);
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || "Invalid email or password",
      };
    }
  };

  const refreshSession = async () => {
    try {
      const response = await axios.post(`${API}/auth/refresh`);
      const newToken = response.data.access_token;
      localStorage.setItem("token", newToken);
      setToken(newToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      return true;
    } catch (error) {
      console.error("Failed to refresh session:", error);
      return false;
    }
  };

  const register = async (email, password, fullName) => {
    try {
      await axios.post(`${API}/auth/register`, {
        email,
        password,
        full_name: fullName,
      });
      return await login(email, password);
    } catch (error) {
      console.error("Registration failed:", error);
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || "Registration failed",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  useEffect(() => {
    logoutRef.current = logout;
  });

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
};
